/**
 * Leitura humana de um valor do JSON CRU do Mercado Livre (Explorador de
 * Endpoints).
 *
 * POR QUE EXISTE: o radar de campos mostrava cada amostra com `String(valor)`.
 * Valor que é objeto — suggested_price, current_price, costs, promotion_detail —
 * virava "[object Object]", e a única parte útil da tela era o JSON (dono,
 * 22/10). Aqui o valor vira uma árvore pequena que a tela desenha:
 *   · dinheiro do ML ({amount, usd_amount}) → "R$ 230,00";
 *   · número solto em chave de dinheiro (selling_fees, shipping_fees, os preços
 *     do boost) → dinheiro também; usd_amount solto → "US$ …";
 *   · objeto → pares chave: valor (a chave fica a do JSON, para casar com a resposta crua);
 *   · lista → contagem + os primeiros itens.
 */

import { FormatBrl, FormatDecimalBr, FormatMoney } from './format';

/** Chaves possíveis num preço do ML. A doc do /suggestions cita também `suggested_price_amount`. */
const MONEY_OBJECT_KEYS = new Set(['amount', 'suggested_price_amount', 'usd_amount', 'currency_id']);
/**
 * Números que são dinheiro na moeda do payload mesmo vindo soltos: custos do
 * /suggestions, preços e descontos em valor do /seller-promotions (doc de
 * gerenciar-ofertas) e o `amount` de um preço com chave a mais.
 */
const MONEY_NUMBER_KEYS = new Set([
    'amount',
    'suggested_price_amount',
    'selling_fees',
    'shipping_fees',
    'price',
    'original_price',
    'discount_meli_boost_amount',
    'total_price_for_boosted_offer',
    'suggested_discounted_price',
    'min_discounted_price',
    'max_discounted_price',
    'top_price',
    'top_deal_price',
]);
/** Quantos itens de uma lista aparecem antes do "e mais N". */
const LIST_PREVIEW = 3;
/** Pares por objeto: payload de catálogo tem dezenas de chaves; o resto fica no JSON. */
const PAIRS_LIMIT = 20;
/** Abaixo desta profundidade o valor vira só a contagem (4 alcança metadata.graph[0].info). */
const DEPTH_LIMIT = 4;
/** Espaço não-separável, o mesmo do FormatMoney: o código da moeda não se separa do número. */
const NBSP = ' ';

/** Número do payload (aceita número em texto); null quando não é número. */
export function FiniteMlNumber(value) {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
}

/** Texto de um valor que a doc diz ser texto (status, ids, datas); null se vier objeto ou lista. */
export function MlScalarText(value) {
    if (typeof value === 'string') {
        return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }
    return null;
}

/** "30%" / "3,5%"; vazio vira traço. */
export function FormatMlPercent(value) {
    const number = FiniteMlNumber(value);
    return number === null ? '—' : `${FormatDecimalBr(number, { maxFractionDigits: 2 })}%`;
}

/** Valor em dinheiro na moeda do payload. BRL (MLB) usa o formatador canônico. */
export function FormatMlMoney(amount, currency = 'BRL') {
    const number = FiniteMlNumber(amount);
    if (number === null) {
        return '—';
    }
    if (typeof currency !== 'string' || !currency || currency === 'BRL') {
        return FormatMoney(number);
    }
    return `${currency}${NBSP}${FormatBrl(number)}`;
}

const formatUsd = (value) => `US$${NBSP}${FormatBrl(value)}`;

/** Preço do ML só com as chaves de preço: `{amount, usd_amount}` (e variações da doc). Vira uma linha. */
export function IsMlMoneyObject(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return false;
    }
    const keys = Object.keys(value);
    const amount = value.amount ?? value.suggested_price_amount;
    return keys.length > 0 && keys.every((key) => MONEY_OBJECT_KEYS.has(key)) && FiniteMlNumber(amount) !== null;
}

/**
 * Montante de um preço do ML, ou null quando não é preço. Chave a mais no
 * objeto não esconde o valor: o resumo mostra o `amount` mesmo assim.
 */
export function MlMoneyAmount(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return null;
    }
    return FiniteMlNumber(value.amount ?? value.suggested_price_amount);
}

function formatMoneyObject(value, currency) {
    const own = typeof value.currency_id === 'string' && value.currency_id ? value.currency_id : currency;
    const main = FormatMlMoney(value.amount ?? value.suggested_price_amount, own);
    const usd = FiniteMlNumber(value.usd_amount);
    // usd_amount 0 é o que o ML manda quando não converte — mostrar "US$ 0,00" só confunde.
    return usd ? `${main} (${formatUsd(usd)})` : main;
}

function formatNumber(value, key, currency) {
    if (key === 'usd_amount') {
        return formatUsd(value);
    }
    if (key && MONEY_NUMBER_KEYS.has(key)) {
        return FormatMlMoney(value, currency);
    }
    if (key && /percent/.test(key)) {
        return FormatMlPercent(value);
    }
    return String(value);
}

const text = (value) => ({ kind: 'text', text: value });

/**
 * Árvore de exibição de um valor do payload:
 *   { kind: 'text', text }
 *   { kind: 'pairs', total, pairs: [{ key, value: <árvore> }] }
 *   { kind: 'list', total, items: [<árvore>] }
 * `key` é a chave em que o valor estava (decide dinheiro e porcentagem);
 * `currency` vem do `currency_id` do payload.
 */
export function DescribeMlValue(value, { key = null, currency = 'BRL', depth = 0 } = {}) {
    if (value === null) {
        return text('null');
    }
    if (value === undefined) {
        return text('—');
    }
    if (typeof value === 'boolean') {
        return text(value ? 'true' : 'false');
    }
    if (typeof value === 'number') {
        return text(formatNumber(value, key, currency));
    }
    if (typeof value !== 'object') {
        return text(String(value));
    }
    if (Array.isArray(value)) {
        if (value.length === 0) {
            return text('lista vazia');
        }
        if (depth >= DEPTH_LIMIT) {
            return text(`lista com ${value.length} ${value.length === 1 ? 'item' : 'itens'}`);
        }
        return {
            kind: 'list',
            total: value.length,
            items: value.slice(0, LIST_PREVIEW).map((item) => DescribeMlValue(item, { currency, depth: depth + 1 })),
        };
    }
    if (IsMlMoneyObject(value)) {
        return text(formatMoneyObject(value, currency));
    }
    const entries = Object.entries(value);
    if (entries.length === 0) {
        return text('objeto vazio');
    }
    if (depth >= DEPTH_LIMIT) {
        return text(`objeto com ${entries.length} ${entries.length === 1 ? 'campo' : 'campos'}`);
    }
    return {
        kind: 'pairs',
        total: entries.length,
        pairs: entries.slice(0, PAIRS_LIMIT).map(([childKey, childValue]) => ({
            key: childKey,
            value: DescribeMlValue(childValue, { key: childKey, currency, depth: depth + 1 }),
        })),
    };
}

/** A mesma árvore numa linha só — para `title` e para conferência em teste. */
export function MlValueText(node) {
    if (!node) {
        return '';
    }
    if (node.kind === 'text') {
        return node.text;
    }
    if (node.kind === 'list') {
        const rest = node.total - node.items.length;
        const preview = node.items.map(MlValueText).join(', ');
        return `${node.total} ${node.total === 1 ? 'item' : 'itens'}: ${preview}${rest > 0 ? ` e mais ${rest}` : ''}`;
    }
    const rest = node.total - node.pairs.length;
    const pairs = node.pairs.map((pair) => `${pair.key}: ${MlValueText(pair.value)}`).join(' · ');
    return `{ ${pairs}${rest > 0 ? ` · e mais ${rest}` : ''} }`;
}

/** 401 "Caller is not the item's owner": o anúncio é de outro vendedor (doc de referencias-de-precos). */
export function IsMlNotOwnerResponse(status, payload) {
    return (
        status === 401 &&
        Boolean(payload) &&
        typeof payload === 'object' &&
        /not the item'?s owner/i.test(String(payload.message ?? ''))
    );
}
